// The Schedule of Work as data: what the PDF prints, worked out from the
// areas and the options chosen (docs/SCHEDULE-OF-WORK.md). Pure — the drawing
// (pdf-*.ts) only lays this out, so every figure here is tested.
import {
  approvedHoursOf,
  areaWashArea,
  deskRequirements,
  isSprayed,
  parseSurfaces,
  PRELIMINARY,
  assessedSurfaceHours,
  pricedApprovedHours,
  readSiteReqs,
  surfaceLabel,
  surfaceTotalsIn,
  washAreaIn,
  washIn,
  type AccessTag,
  type MeasuredSurface,
  type OptionHours,
  type SurfacesDoc,
  type SurfaceType,
  type TickRow,
} from '@higgins/m2q-pricing';
import { hireNames, NO_LINE_NAMES, prelimName, type LineNames } from './priced-names.ts';
import { leftOutIn, scheduleSurface, type ScheduleSystems } from './systems.ts';

/** Scope only, measured, or approved: the hours each area is priced on (the old PM-assessed option went on 27 Oct). */
export type HoursSource = 'none' | 'measured' | 'approved';

export interface ScheduleOptions {
  hoursSource: HoursSource;
  showM2: boolean;
  showHours: boolean;
  showLitres: boolean;
  showSurfaces: boolean;
  /** The areas to include, in the order to print them. */
  areaIds: string[];
  /** Lead-in sentence per access category; a missing one uses the default. */
  leadIns: Record<string, string>;
  includePhotos: boolean;
  coverPhotoId: string | null;
  leftOutPhotoIds: string[];
}

/** An area as the device holds it: its id and its CRM columns. */
export interface ScheduleAreaInput {
  id: string;
  fields: Record<string, string | number | boolean | null>;
  /** Its ticks as rows; none = read from hig_sitereqs (SCHEMA.md "Site requirements"). */
  tickRows?: readonly TickRow[];
  /** Its building: a count above 1 multiplies every figure, as pricing does (#526). None = one copy. */
  building?: { name: string; count: number };
}

/** A photo of an area: on the device (waiting to upload) or on the job site. */
export interface PhotoRef {
  id: string;
  areaId: string;
  surfaceId: string | null;
  /** "Walls 01.jpg". */
  name: string;
  /** The way the camera faced (N … NW), when known. */
  direction?: string | null;
}

/** A photo's caption: its name, and the way the camera faced ("Walls 01 · facing NE"). */
export function photoCaption(photo: PhotoRef): string {
  const name = photo.name.replace(/\.jpe?g$/i, '');
  return photo.direction ? `${name} · facing ${photo.direction}` : name;
}

export interface SurfaceRow {
  /** `wash`: the area's wash m² and hours (#510), after its surfaces. */
  kind: 'surface' | 'group' | 'subtotal' | 'wash';
  label: string;
  quantity?: number;
  unit?: string;
  hours?: number;
  litres?: number;
  /** Its paint systems when it has more than one (#412): "2 coats Durebuild; 2 coats Weathermax". */
  systems?: string;
}

interface AccessBlock {
  category: string;
  leadIn: string;
  items: string[];
}

export interface ScheduleArea {
  id: string;
  name: string;
  side: string;
  rows: SurfaceRow[];
  /** m² of every surface, as the wash m² counts it (areaWashArea: a run at its girth); hours from the chosen source, down time added; measured litres. */
  totals: { m2: number; hours: number; litres: number };
  /** Says where the area's hours came from when it isn't plain measured. */
  hoursNote: string | null;
  /** The hours column's heading: "Hours", or what the rows are when the approved hours make it matter ("Assessed hours"). */
  hoursLabel: string;
  /** The total row's label: "Area total", or "Area total (other: <reason>)" for Other approved hours. */
  totalLabel: string;
  scopeNotes: string | null;
  exclusions: string | null;
  defects: string | null;
  access: AccessBlock[];
  /** The free-text access notes (hig_accessneeds). */
  accessNotes: string | null;
  photos: PhotoRef[];
  /** A surface here has paint systems from the option named on the cover. */
  hasSystems: boolean;
}

/** Site2Scope's lead-ins (ScopeReport.js:792-799), plus one for preliminaries. */
export const DEFAULT_LEAD_INS: Record<string, string> = {
  'Internal Access': 'The following internal access requirements have been identified for this area:',
  'Internal Conditions': 'The following internal conditions apply to this area:',
  'External Access & Height': 'The following access and working-at-height requirements have been identified for this area:',
  'Site Hazards': 'The following site hazards have been identified and must be managed:',
  'Building & Condition': 'The following building condition issues have been noted:',
  'Site Conditions': 'The following site conditions apply to this area:',
  Preliminary: 'Allowed for in this area:',
};
const FALLBACK_LEAD_IN = 'The following items apply:';

const PERCENT = 100;
const M2 = 'm²';
const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);
const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

export const leadInFor = (category: string, leadIns: Record<string, string>): string =>
  text(leadIns[category]) ?? DEFAULT_LEAD_INS[category] ?? FALLBACK_LEAD_IN;

/** A category's heading in the Schedule: the preliminaries go by STM002's own word, "Preliminaries" (DD, 2026-10-26). */
export const categoryHeading = (category: string): string => (category === PRELIMINARY ? 'Preliminaries' : category);

function readSurfaces(value: unknown): SurfacesDoc {
  try {
    return parseSurfaces(typeof value === 'string' ? value : null);
  } catch {
    return parseSurfaces(null);
  }
}

/** How the area's hours are worked out and labelled: the total before down time, and what the rows show. */
interface AreaHours {
  hours: number;
  note: string | null;
  /** Assessed: the PM's hours fixed at review, by surface; those rows show them, so the rows add up to the total. */
  fixed?: Readonly<Record<string, number>>;
  label?: string;
  totalLabel?: string;
}

const HOURS_LABEL = 'Hours';
const TOTAL_LABEL = 'Area total';

/**
 * The approved hours as the REVIEW chose them (approved-hours.ts), with rows that show the hours the price used
 * (DD, 2026-10-27): Assessed rows are the PM's hours fixed at review, and every other surface's own (its measured
 * hours and the cover option's extra paint systems, #607); Other has no split per surface, so its rows stay measured
 * and its total says it was set at review; Measured is measured.
 */
function approvedAreaHours(area: ScheduleAreaInput, doc: SurfacesDoc, types: ReadonlyMap<string, SurfaceType>, hours: OptionHours, measured: number): AreaHours {
  const approved = approvedHoursOf(area.fields, doc, types, hours);
  const figure = pricedApprovedHours(area.fields, doc, hours.kept, hours.own);
  if (figure === null) return { hours: measured, note: approved.chosen ? 'Approved hours: the measured hours.' : 'No approved hours — measured hours.' };
  if (approved.stale) return { hours: figure, note: "Assessed was chosen before the PM's hours were fixed per surface: choose it again on the REVIEW." };
  if (approved.source === 'Assessed') {
    // The PM's hours are fixed when chosen: a later change is the REVIEW's to price, never the Schedule's.
    const note = approved.changed
      ? "Assessed hours: the PM's as fixed at review, measured where a surface wasn't assessed. The PM has changed their hours since; choosing Assessed again on the REVIEW prices them."
      : "Assessed hours: the PM's, measured where a surface wasn't assessed.";
    return { hours: figure, note, fixed: doc.approvedAssessed, label: 'Assessed hours' };
  }
  const reason = approved.reason.trim();
  return { hours: figure, note: 'The total was set at review.', label: 'Measured hours', totalLabel: reason ? `${TOTAL_LABEL} (other: ${reason})` : `${TOTAL_LABEL} (other)` };
}

/**
 * The area's hours before down time from the chosen source, over the surfaces the cover option keeps, each at its hours
 * in that option (`hours`: its coats, rate and every paint system's, MJ 2026-10-20, as pricing works them out);
 * approved falls back to those where the area has none.
 */
function baseHours(area: ScheduleAreaInput, doc: SurfacesDoc, types: ReadonlyMap<string, SurfaceType>, source: HoursSource, hours: OptionHours): AreaHours {
  const measured = hours.kept.reduce((sum, s) => sum + hours.own(s), 0);
  if (source === 'approved') return approvedAreaHours(area, doc, types, hours, measured);
  return { hours: measured, note: null };
}

/** What a surface's row needs besides the surface: the area's document and id, the types, the option, and the surfaces it keeps. */
interface RowContext {
  doc: SurfacesDoc;
  areaId: string;
  types: ReadonlyMap<string, SurfaceType>;
  systems: ScheduleSystems | null;
  kept: readonly MeasuredSurface[];
  /** Assessed: the PM's hours fixed at review, which those surfaces' rows show in place of their own. */
  fixed: Readonly<Record<string, number>> | undefined;
}

function surfaceRow(ctx: RowContext, s: MeasuredSurface): SurfaceRow {
  const type = ctx.types.get(s.typeId);
  const t = surfaceTotalsIn(ctx.doc, s, ctx.types);
  const own = scheduleSurface(ctx.areaId, ctx.doc, s, type, ctx.systems);
  const sprayed = isSprayed(s, type);
  return {
    kind: 'surface',
    label: `${surfaceLabel(s)}${sprayed ? ' (spray)' : ''}`,
    quantity: t.quantity,
    unit: s.unit,
    hours: assessedSurfaceHours(ctx.fixed ?? {}, s, () => own.hours),
    litres: own.litres,
    ...(own.text && { systems: own.text }),
  };
}

/** Ungrouped surfaces, then each repeat group with its header and subtotal: the surfaces the option keeps. */
function surfaceRows(ctx: RowContext): SurfaceRow[] {
  const { doc, kept } = ctx;
  const groupIds = new Set(doc.groups.map((g) => g.id));
  const rows = kept.filter((s) => !s.groupId || !groupIds.has(s.groupId)).map((s) => surfaceRow(ctx, s));
  for (const g of doc.groups) {
    const members = kept.filter((s) => s.groupId === g.id).map((s) => surfaceRow(ctx, s));
    if (!members.length) continue;
    rows.push({ kind: 'group', label: `${g.name} × ${g.count}` }, ...members);
    rows.push({ kind: 'subtotal', label: `${g.name} subtotal`, hours: members.reduce((sum, r) => sum + (r.hours ?? 0), 0), litres: members.reduce((sum, r) => sum + (r.litres ?? 0), 0) });
  }
  return rows;
}

/** What an area's ticks are named from: the Schedule's option and what its lines' names are read from (#464). */
interface PricedContext {
  systems: ScheduleSystems | null;
  names: LineNames;
  washRate: number | null;
}

/**
 * The area's ticked site requirements, by category in list order, with each category's lead-in.
 * A preliminary or hire item goes by the name of the line priced for it, else the ticked name (#464, priced-names.ts).
 */
function accessBlocks(area: ScheduleAreaInput, tags: readonly AccessTag[], leadIns: Record<string, string>, priced: PricedContext): AccessBlock[] {
  let doc;
  try {
    doc = readSiteReqs({ json: text(area.fields.hig_sitereqs), rows: area.tickRows ?? [] }, tags);
  } catch {
    return [];
  }
  const byCategory = new Map<string, string[]>();
  const add = (category: string, ...items: string[]) => byCategory.set(category, [...(byCategory.get(category) ?? []), ...items]);
  const choice = priced.systems?.scope.areas?.[area.id];
  const prelims = deskRequirements(doc, tags).prelims;
  const typedPrelims = new Map(prelims.filter((p) => !doc.tags.includes(p.key)).map((p) => [p.label, p]));
  const named = (tag: AccessTag): string[] => {
    if (tag.category === PRELIMINARY) return [prelimName(tag, choice, priced.names)];
    return tag.hireSearch ? hireNames(tag, choice, priced.names) : [tag.label];
  };
  for (const tag of [...tags].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (doc.tags.includes(tag.key)) add(tag.category, ...named(tag));
  }
  for (const other of doc.other) {
    const prelim = other.category === PRELIMINARY ? typedPrelims.get(other.label) : undefined;
    add(other.category, prelim ? prelimName(prelim, choice, priced.names) : other.label);
  }
  return [...byCategory].map(([category, items]) => ({ category, leadIn: leadInFor(category, leadIns), items }));
}

/**
 * The area's wash (#510), as the option named on the cover prices it, else as set on site: its m² (typed for the
 * option, else the total of the surfaces the option keeps) and hours at the option's wash rate, else the setting's;
 * null when not washed.
 */
function washOf(area: ScheduleAreaInput, doc: SurfacesDoc, types: ReadonlyMap<string, SurfaceType>, systems: ScheduleSystems | null, washRate: number | null) {
  const choice = systems?.scope.areas?.[area.id];
  const m2 = washAreaIn(area, doc, choice, areaWashArea(doc, types, leftOutIn(area.id, systems))) ?? 0;
  if (!(m2 > 0)) return null;
  const rate = choice?.washRate ?? systems?.scope.rates?.wash?.sell ?? washRate;
  return { m2, hours: rate ? m2 / rate : null };
}

/** A building's count as pricing reads it (pricing-inputs.ts): at least 1. */
export const buildingCountOf = (value: unknown): number => Math.max(1, num(value) ?? 1);

const times = (n: number | undefined, count: number) => (n === undefined ? undefined : n * count);

/**
 * A repeated building's area: every quantity, hours and litres figure × the building's count, the same rule
 * pricing uses (countedSection; DD, 2026-10-26, #526). Labels, units and paint systems are unchanged.
 */
function counted(rows: SurfaceRow[], count: number): SurfaceRow[] {
  if (count === 1) return rows;
  return rows.map((r) => ({ ...r, quantity: times(r.quantity, count), hours: times(r.hours, count), litres: times(r.litres, count) }));
}

/** "Lobby (Block B × 3)" for a building counted more than once; the plain name otherwise. */
const countedName = (name: string, building: ScheduleAreaInput['building']) =>
  building && building.count > 1 ? `${name} (${building.name} × ${building.count})` : name;

/** A wash-only area's surfaces: measured, but no painting hours, litres or paint systems. */
const unpainted = (rows: SurfaceRow[]): SurfaceRow[] => rows.map(({ hours: _h, litres: _l, systems: _s, ...row }) => row);

function scheduleArea(
  area: ScheduleAreaInput,
  types: ReadonlyMap<string, SurfaceType>,
  tags: readonly AccessTag[],
  photos: readonly PhotoRef[],
  options: ScheduleOptions,
  priced: PricedContext,
): ScheduleArea {
  const { systems } = priced;
  const doc = readSurfaces(area.fields.hig_surfaces);
  const wash = washOf(area, doc, types, systems, priced.washRate);
  // As pricing decides it, so a wash-only area with no wash m² is still wash only (no painting hours or litres).
  const { washOnly } = washIn(area, doc, systems?.scope.areas?.[area.id]);
  // The surfaces the cover option keeps, each at its hours there, as pricing works them out (PR #609 review).
  const leftOut = leftOutIn(area.id, systems);
  const kept = doc.surfaces.filter((s) => !leftOut.has(s.id));
  const own = (s: MeasuredSurface) => scheduleSurface(area.id, doc, s, types.get(s.typeId), systems).hours;
  // Wash only: the approved hours are painting hours, so only the wash's count.
  const base: AreaHours = washOnly ? { hours: 0, note: 'Wash only: the wash hours.' } : baseHours(area, doc, types, options.hoursSource, { kept, own });
  const measured = surfaceRows({ doc, areaId: area.id, types, systems, kept, fixed: base.fixed });
  const washRow: SurfaceRow[] = wash ? [{ kind: 'wash', label: 'Wash', quantity: wash.m2, unit: M2, ...(wash.hours !== null && { hours: wash.hours }) }] : [];
  const count = area.building?.count ?? 1;
  const rows = counted([...(washOnly ? unpainted(measured) : measured), ...washRow], count);
  const surfaceOnly = rows.filter((r) => r.kind === 'surface');
  const downtime = (num(area.fields.hig_downtimepercent) ?? 0) / PERCENT;
  const left = new Set(options.leftOutPhotoIds);
  const name = countedName(text(area.fields.hig_name) ?? 'Unnamed area', area.building);
  return {
    id: area.id,
    name: washOnly ? `${name} — wash only` : name,
    side: area.fields.hig_side === 'Internal' ? 'Internal' : 'External',
    rows,
    totals: {
      // Every surface the rows list, LM runs at their girth: the wash m² the desk and proposal show (DD, 2026-10-26).
      m2: areaWashArea(doc, types, leftOut) * count,
      // Measured or approved, both per copy of the building, like pricing's approved hours (#526).
      hours: (base.hours + (wash?.hours ?? 0)) * (1 + downtime) * count,
      litres: surfaceOnly.reduce((sum, r) => sum + (r.litres ?? 0), 0),
    },
    hoursNote: [base.note, downtime > 0 ? `Includes ${Math.round(downtime * PERCENT)}% down time.` : null].filter(Boolean).join(' ') || null,
    hoursLabel: base.label ?? HOURS_LABEL,
    totalLabel: base.totalLabel ?? TOTAL_LABEL,
    scopeNotes: text(area.fields.hig_scopenotes),
    exclusions: text(area.fields.hig_exclusions),
    defects: text(area.fields.hig_defectsnote),
    access: accessBlocks(area, tags, options.leadIns, priced),
    accessNotes: text(area.fields.hig_accessneeds),
    photos: options.includePhotos ? photos.filter((p) => p.areaId === area.id && !left.has(p.id)) : [],
    hasSystems: surfaceOnly.some((r) => r.systems),
  };
}

/**
 * The included areas, in the chosen order, internal before external (each side keeps the chosen order).
 * `systems`: the option whose paint systems and priced lines they show; `names`: what those lines' names are read from;
 * `washRate`: the branch's wash.rate setting, when the device could read it (#510).
 */
export function scheduleAreas(
  areas: readonly ScheduleAreaInput[],
  types: ReadonlyMap<string, SurfaceType>,
  tags: readonly AccessTag[],
  photos: readonly PhotoRef[],
  options: ScheduleOptions,
  systems: ScheduleSystems | null = null,
  names: LineNames = NO_LINE_NAMES,
  washRate: number | null = null,
): ScheduleArea[] {
  const byId = new Map(areas.map((a) => [a.id, a]));
  const priced = { systems, names, washRate };
  const built = options.areaIds.map((id) => byId.get(id)).filter((a): a is ScheduleAreaInput => a !== undefined).map((a) => scheduleArea(a, types, tags, photos, options, priced));
  return [...built.filter((a) => a.side === 'Internal'), ...built.filter((a) => a.side !== 'Internal')];
}

/** Totals of a list of areas. */
export const totalsOf = (areas: readonly ScheduleArea[]) =>
  areas.reduce((t, a) => ({ m2: t.m2 + a.totals.m2, hours: t.hours + a.totals.hours, litres: t.litres + a.totals.litres }), { m2: 0, hours: 0, litres: 0 });

/** "SA2007 Schedule of Work v1 2026-10-13.pdf". */
export const scheduleFileName = (jobNumber: string, versionNumber: number | null, isoDate: string): string =>
  `${jobNumber} Schedule of Work${versionNumber ? ` v${versionNumber}` : ''} ${isoDate}.pdf`;
